import type { LimitUse, TenantPlanView } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatBytes, formatNumber, formatUsd, Meter } from "@/components/charts";
import { Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, ok } from "../api-errors.ts";
import { useAdminApi } from "./admin-api.tsx";

/**
 * Plan and usage (spec `tenant-plans` "Limits are visible to the tenant"): the plan's name, each
 * effective limit and this month's use, with a warning from 80 % of a limit. Tenant admins and,
 * read-only like everything here, entity admins. Limits are set by the instance's operators.
 */
export function PlanSection() {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const plan = useQuery({
    queryKey: ["admin", "plan"],
    queryFn: async (): Promise<TenantPlanView> => ok(await admin.plan.$get()),
    retry: false,
  });
  const forbidden = plan.error instanceof ApiError && plan.error.status === 403;
  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={t("plan.title")}
        lead={plan.data ? t("plan.lead", { name: plan.data.plan.name }) : t("plan.hint")}
      />
      {forbidden ? (
        <Notice>{t("plan.adminsOnly")}</Notice>
      ) : plan.isError ? (
        <Notice tone="danger">{plan.error.message}</Notice>
      ) : plan.data ? (
        <PlanFigures view={plan.data} />
      ) : (
        <Skeleton className="h-48 w-full" />
      )}
    </div>
  );
}

function PlanFigures({ view }: { view: TenantPlanView }) {
  const { t } = useTranslation();
  const rows: { key: string; label: string; use: LimitUse; format: (n: number) => string }[] = [
    { key: "seats", label: t("plan.seats"), use: view.seats, format: (n) => formatNumber(n) },
    { key: "storage", label: t("plan.storage"), use: view.storageBytes, format: formatBytes },
    { key: "ocr", label: t("plan.ocrPages"), use: view.ocrPages, format: (n) => formatNumber(n) },
    { key: "spend", label: t("plan.modelSpend"), use: view.modelSpendUsd, format: formatUsd },
  ];
  // Synthetic demo spend (demo-world-v2), already in `used`: said in words wherever it counts.
  const synthetic = view.modelSpendUsd.syntheticUsd ?? 0;
  const reached = rows.filter((r) => r.use.reached);
  const warned = rows.filter((r) => r.use.warning && !r.use.reached);
  return (
    <div className="flex flex-col gap-4">
      {reached.length > 0 ? (
        <Notice tone="danger" title={t("plan.reachedTitle")}>
          {reached.map((r) => r.label).join(", ")}
          {". "}
          {view.modelSpendUsd.reached && view.plan.modelLimitMode === "hard"
            ? t("plan.hardSpend")
            : t("plan.reachedBody")}
        </Notice>
      ) : null}
      {warned.length > 0 ? (
        <Notice tone="warning" title={t("plan.warningTitle")}>
          {t("plan.warningBody", { limits: warned.map((r) => r.label).join(", ") })}
        </Notice>
      ) : null}
      {synthetic > 0 ? (
        <Notice tone="info" title={t("plan.synthetic")}>
          {t("plan.syntheticSpend", { amount: formatUsd(synthetic) })}
        </Notice>
      ) : null}
      <section
        aria-labelledby="plan-use"
        className="flex flex-col gap-5 rounded-lg border border-line bg-surface-raised p-5"
      >
        <h2 id="plan-use" className="text-sm font-semibold text-fg">
          {t("plan.thisMonth", { month: view.month })}
        </h2>
        {rows.map((r) => (
          <Meter
            key={r.key}
            label={r.label}
            value={r.use.used}
            max={r.use.limit}
            formatValue={r.format}
            {...(r.use.limit === null && {
              valueText: `${r.format(r.use.used)} · ${t("plan.unlimited")}`,
            })}
          />
        ))}
        {synthetic > 0 ? <p className="text-xs text-fg-muted">{t("plan.synthetic")}</p> : null}
        {view.modelSpendUsd.unknownCostCalls > 0 ? (
          <p className="text-xs text-fg-muted">
            {t("plan.unknownCost", { count: view.modelSpendUsd.unknownCostCalls })}
          </p>
        ) : null}
        <p className="text-xs text-fg-muted">
          {view.plan.modelLimitMode === "hard" ? t("plan.modeHard") : t("plan.modeSoft")}
        </p>
      </section>
    </div>
  );
}
